import type { StrategyTicketContext } from '../strategy-backtest/types/context';

const STORAGE_VERSION = 'v1';

const RESERVED_PATH_SEGMENTS = new Set([
  '',
  'chat',
  'trading',
  'home',
  'about',
  'privacy',
  'terms',
]);

const canUseSessionStorage = (): boolean => {
  try {
    return typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined';
  } catch {
    return false;
  }
};

export const detectSessionIdFromPath = (pathname: string): string | undefined => {
  const pathParts = pathname.split('/');
  const firstSegment = pathParts[1] ?? '';
  if (!firstSegment || RESERVED_PATH_SEGMENTS.has(firstSegment)) return undefined;
  if (firstSegment.length <= 10) return undefined;
  return firstSegment;
};

export const buildOrderTicketPersistenceScope = (
  pathname: string,
  strategyContext: StrategyTicketContext,
): string => {
  const sessionId = detectSessionIdFromPath(pathname);
  if (sessionId) {
    return `chat:${sessionId}`;
  }

  return `context:${strategyContext}`;
};

export const buildManualOrderStorageKey = (scope: string): string =>
  `order-ticket-manual:${scope}:${STORAGE_VERSION}`;

export const buildStrategyOrderStorageKey = (scope: string): string =>
  `order-ticket-strategy:${scope}:${STORAGE_VERSION}`;

export const buildOrderTicketPaneModeStorageKey = (scope: string): string =>
  `order-ticket-pane-mode:${scope}:${STORAGE_VERSION}`;

const LEGACY_DASHBOARD_SCOPE = 'context:dashboard';
const TRADING_SCOPE = 'context:trading';

const migrateSessionStorageKey = (sourceKey: string, targetKey: string): void => {
  try {
    const sourceRaw = window.sessionStorage.getItem(sourceKey);
    if (sourceRaw === null) return;

    const targetRaw = window.sessionStorage.getItem(targetKey);
    if (targetRaw === null) {
      window.sessionStorage.setItem(targetKey, sourceRaw);
    }

    window.sessionStorage.removeItem(sourceKey);
  } catch {
    // Ignore migration failures (quota/private mode/etc.).
  }
};

/**
 * Dashboard route was retired in favor of /trading. Migrate any
 * dashboard-scoped order ticket drafts into the trading scope.
 */
export const migrateLegacyDashboardOrderTicketStorage = (): void => {
  if (!canUseSessionStorage()) return;

  migrateSessionStorageKey(
    buildManualOrderStorageKey(LEGACY_DASHBOARD_SCOPE),
    buildManualOrderStorageKey(TRADING_SCOPE),
  );
  migrateSessionStorageKey(
    buildStrategyOrderStorageKey(LEGACY_DASHBOARD_SCOPE),
    buildStrategyOrderStorageKey(TRADING_SCOPE),
  );
  migrateSessionStorageKey(
    buildOrderTicketPaneModeStorageKey(LEGACY_DASHBOARD_SCOPE),
    buildOrderTicketPaneModeStorageKey(TRADING_SCOPE),
  );
};

export const readSessionStorageJson = <T>(key: string): T | null => {
  if (!canUseSessionStorage()) return null;

  try {
    const raw = window.sessionStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
};

export const writeSessionStorageJson = (key: string, value: unknown): void => {
  if (!canUseSessionStorage()) return;

  try {
    window.sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Ignore storage write failures (quota/private mode/etc.).
  }
};

export const removeSessionStorageKey = (key: string): void => {
  if (!canUseSessionStorage()) return;

  try {
    window.sessionStorage.removeItem(key);
  } catch {
    // Ignore storage removal failures.
  }
};
